import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Minus, RefreshCw } from 'lucide-react';
import { Badge, Button, Card } from '@/components/ui';
import { KPICard } from './components/KPICard';
import { TrendChart } from './components/TrendChart';
import {
  type AccuracyRating,
  type HouseholdAccuracy,
  useAdminHouseholdAccuracy,
} from './hooks/useAdminHouseholdAccuracy';

type SortKey = 'precision' | 'trend' | 'decided';

/** "90%", or "—" when there is nothing to divide. */
function formatPct(ratio: number | null): string {
  if (ratio === null) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/** "Oct 2, 2026" from an ISO timestamp; the raw value if unparseable. */
function formatSince(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Share of decided items, as a percentage string. */
function share(n: number, decided: number): string {
  return decided > 0 ? `${Math.round((n / decided) * 100)}%` : '—';
}

const RATING_VARIANT: Record<
  AccuracyRating,
  'success' | 'info' | 'warning' | 'danger'
> = {
  A: 'success',
  B: 'info',
  C: 'warning',
  D: 'danger',
};

function RatingBadge({ rating }: { rating: AccuracyRating | null }) {
  if (rating === null) {
    return (
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        Not enough data
      </span>
    );
  }
  return (
    <Badge
      variant={RATING_VARIANT[rating]}
      size="sm"
    >
      {rating}
    </Badge>
  );
}

function TrendCell({ trend }: { trend: number | null }) {
  if (trend === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const points = Math.round(trend * 100);
  if (points === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-muted-foreground"
        aria-label="No change"
      >
        <Minus
          size={14}
          aria-hidden="true"
        />
        0 pts
      </span>
    );
  }
  const up = points > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 ${up ? 'text-success' : 'text-danger'}`}
      aria-label={up ? `Up ${points} points` : `Down ${-points} points`}
    >
      {up ? (
        <ArrowUp
          size={14}
          aria-hidden="true"
        />
      ) : (
        <ArrowDown
          size={14}
          aria-hidden="true"
        />
      )}
      {up ? '+' : ''}
      {points} pts
    </span>
  );
}

/**
 * The family's learning curve as "60% → 90%": precision per position bucket
 * (items 1–10, 11–20, 21–40, 41+), oldest first. Going up means Yaruta needs
 * fewer corrections the more the family uses it.
 */
function curveText(h: HouseholdAccuracy): string {
  if (h.curve.length === 0) return '—';
  return h.curve.map((c) => formatPct(c.precision)).join(' → ');
}

function curveTitle(h: HouseholdAccuracy): string {
  if (h.curve.length === 0) return 'No decided items yet';
  return h.curve
    .map(
      (c) =>
        `items ${c.bucket}: ${formatPct(c.precision)} (${c.unchanged}/${c.decided})`,
    )
    .join(' · ');
}

function householdLabel(h: HouseholdAccuracy): string {
  return h.primary_email ?? h.primary_name ?? h.household_id.slice(0, 8);
}

function memoryTotal(facts: Record<string, number>): number {
  return Object.values(facts).reduce((s, n) => s + n, 0);
}

function memoryTitle(facts: Record<string, number>): string {
  const parts = Object.entries(facts).map(([k, n]) => `${k}: ${n}`);
  return parts.length > 0 ? parts.join(' · ') : 'No active facts';
}

/** Nulls last, whatever the direction. */
function compare(a: number | null, b: number | null, desc: boolean): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return desc ? b - a : a - b;
}

interface AdminAccuracyProps {
  password: string;
}

export function AdminAccuracy({ password }: AdminAccuracyProps) {
  const { status, data, error, lastFetchedAt, refetch } =
    useAdminHouseholdAccuracy(password);
  const [sortKey, setSortKey] = useState<SortKey>('precision');
  const [desc, setDesc] = useState(false);

  const byId = useMemo(
    () => new Map((data?.households ?? []).map((h) => [h.household_id, h])),
    [data],
  );

  const sorted = useMemo(() => {
    const rows = [...(data?.households ?? [])];
    rows.sort((a, b) => {
      const primary =
        sortKey === 'precision'
          ? compare(a.precision, b.precision, desc)
          : sortKey === 'trend'
            ? compare(a.trend, b.trend, desc)
            : compare(a.decided, b.decided, desc);
      return primary || a.household_id.localeCompare(b.household_id);
    });
    return rows;
  }, [data, sortKey, desc]);

  /** Every household's weeks pooled, for the overall line. */
  const overallWeeks = useMemo(() => {
    const byWeek = new Map<string, { decided: number; unchanged: number }>();
    for (const h of data?.households ?? []) {
      for (const w of h.weeks) {
        const acc = byWeek.get(w.week_start) ?? { decided: 0, unchanged: 0 };
        acc.decided += w.decided;
        acc.unchanged += w.unchanged;
        byWeek.set(w.week_start, acc);
      }
    }
    return [...byWeek.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .filter(([, v]) => v.decided > 0)
      .map(([label, v]) => ({
        label,
        count: Math.round((v.unchanged / v.decided) * 100),
      }));
  }, [data]);

  const setSort = (key: SortKey) => {
    if (key === sortKey) {
      setDesc((d) => !d);
    } else {
      setSortKey(key);
      // Lowest precision first surfaces problems; biggest trend / volume first.
      setDesc(key !== 'precision');
    }
  };

  const sortLabel = (key: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => setSort(key)}
      className={`inline-flex items-center gap-1 font-medium cursor-pointer ${
        sortKey === key ? 'text-foreground' : 'text-muted-foreground'
      }`}
      aria-label={`Sort by ${label}`}
    >
      {label}
      {sortKey === key ? (
        desc ? (
          <ArrowDown
            size={12}
            aria-hidden="true"
          />
        ) : (
          <ArrowUp
            size={12}
            aria-hidden="true"
          />
        )
      ) : null}
    </button>
  );

  if (status === 'loading' && !data) {
    return (
      <div className="text-muted-foreground">Loading household accuracy…</div>
    );
  }
  if (status === 'error' && !data) {
    return (
      <Card className="border-danger">
        <div className="text-sm text-danger">
          Failed to load household accuracy: {error}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          className="mt-3"
        >
          Retry
        </Button>
      </Card>
    );
  }
  if (!data) return null;

  const rated = data.households.filter((h) => h.rating !== null).length;

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          Last refreshed:{' '}
          {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '—'}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          disabled={status === 'loading'}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${status === 'loading' ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Extraction accuracy</h2>
        <p className="text-sm text-muted-foreground m-0">
          Share of AI-extracted items parents approved without changing the
          title, the date, the children, the location or the type, over the last
          30 days. Update and cancel proposals are counted apart. The learning
          curve shows precision over each family's items in the order they were
          decided (1–10, 11–20, 21–40, 41+).
        </p>
        <p
          className="text-xs text-muted-foreground m-0"
          data-testid="dismissals-footnote"
        >
          {data.dismissals_since
            ? `Discarded items are counted from ${formatSince(data.dismissals_since)}; earlier single-item discards were not recorded, so earlier precision reads high.`
            : 'Discarded items are counted from the first one recorded after this release; earlier single-item discards were not recorded.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KPICard
            label="Overall precision"
            value={formatPct(data.overall.precision)}
            hint={`${data.overall.unchanged} of ${data.overall.decided} items unchanged`}
          />
          <KPICard
            label="Families rated"
            value={`${rated} / ${data.households.length}`}
            hint="Rated with 10+ decided items in 30 days"
          />
          <KPICard
            label="Context level"
            value={data.context_level}
            hint="The ceiling. Which families get it: Family Context tab"
          />
        </div>
      </section>

      {data.households.length === 0 ? (
        <Card
          padding="lg"
          className="text-center"
        >
          <div className="text-muted-foreground text-sm">
            No decided review items yet — accuracy appears once parents approve
            or dismiss extracted items.
          </div>
        </Card>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="flex flex-col gap-2 md:col-span-1">
              <span className="text-sm text-muted-foreground">
                Weekly precision, all families
              </span>
              <TrendChart
                data={overallWeeks}
                emptyHint="Nothing decided in the last 4 weeks"
              />
            </Card>
            <Card className="flex flex-col gap-2">
              <span className="text-sm text-muted-foreground">
                Most improved
              </span>
              {data.most_improved.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No trend yet — needs 5+ decided items this week and before.
                </span>
              ) : (
                <ul className="flex flex-col gap-1 m-0 p-0 list-none">
                  {data.most_improved.map((id) => {
                    const h = byId.get(id);
                    if (!h) return null;
                    return (
                      <li
                        key={id}
                        className="flex items-center justify-between gap-2 text-sm min-w-0"
                      >
                        <span className="truncate">{householdLabel(h)}</span>
                        <TrendCell trend={h.trend} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
            <Card className="flex flex-col gap-2">
              <span className="text-sm text-muted-foreground">
                Needs attention
              </span>
              {data.needs_attention.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No rated family below A.
                </span>
              ) : (
                <ul className="flex flex-col gap-1 m-0 p-0 list-none">
                  {data.needs_attention.map((id) => {
                    const h = byId.get(id);
                    if (!h) return null;
                    return (
                      <li
                        key={id}
                        className="flex items-center justify-between gap-2 text-sm min-w-0"
                      >
                        <span className="truncate">{householdLabel(h)}</span>
                        <span className="font-medium">
                          {formatPct(h.precision)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold">By family</h2>
            <Card
              variant="default"
              padding="none"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="p-3 font-medium">Family</th>
                      <th className="p-3 font-medium">Rating</th>
                      <th className="p-3 text-right">
                        {sortLabel('precision', 'Precision')}
                      </th>
                      <th className="p-3 text-right">
                        {sortLabel('trend', 'Trend')}
                      </th>
                      <th className="p-3 text-right">
                        {sortLabel('decided', 'Decided')}
                      </th>
                      <th className="p-3 font-medium">Last 4 weeks</th>
                      <th className="p-3 font-medium">Learning curve</th>
                      <th className="p-3 font-medium text-right">Dismissed</th>
                      <th className="p-3 font-medium text-right">Child</th>
                      <th className="p-3 font-medium text-right">Title</th>
                      <th className="p-3 font-medium text-right">Date</th>
                      <th className="p-3 font-medium text-right">Location</th>
                      <th className="p-3 font-medium text-right">Type</th>
                      <th className="p-3 font-medium text-right">Late edits</th>
                      <th className="p-3 font-medium text-right">Proposals</th>
                      <th className="p-3 font-medium text-right">Learned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((h) => (
                      <tr
                        key={h.household_id}
                        data-testid="accuracy-row"
                        className="border-b border-border last:border-b-0"
                      >
                        <td className="p-3 text-foreground max-w-[220px] truncate">
                          {householdLabel(h)}
                        </td>
                        <td className="p-3">
                          <RatingBadge rating={h.rating} />
                        </td>
                        <td className="p-3 text-right font-medium">
                          {formatPct(h.precision)}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <TrendCell trend={h.trend} />
                        </td>
                        <td className="p-3 text-right">{h.decided}</td>
                        <td className="p-3 text-muted-foreground whitespace-nowrap">
                          {h.weeks
                            .map((w) => formatPct(w.precision))
                            .join(' · ')}
                        </td>
                        <td
                          className="p-3 text-muted-foreground whitespace-nowrap"
                          title={curveTitle(h)}
                          data-testid="accuracy-curve"
                        >
                          {curveText(h)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share(h.breakdown.dismissed, h.decided)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share(h.breakdown.child_corrected, h.decided)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share(h.breakdown.title_edited, h.decided)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share(h.breakdown.date_corrected, h.decided)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share(h.breakdown.location_corrected, h.decided)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share(h.breakdown.type_changed, h.decided)}
                        </td>
                        <td
                          className="p-3 text-right text-muted-foreground whitespace-nowrap"
                          title="items edited / deleted on the calendar after approval"
                        >
                          {h.late.edited} / {h.late.deleted}
                        </td>
                        <td
                          className="p-3 text-right text-muted-foreground whitespace-nowrap"
                          title={`accepted / dismissed / kept as new · ${h.proposals.refused} update claim(s) refused at extraction`}
                        >
                          {h.proposals.accepted} / {h.proposals.dismissed} /{' '}
                          {h.proposals.kept_as_new}
                        </td>
                        <td
                          className="p-3 text-right text-muted-foreground"
                          title={memoryTitle(h.memory_facts)}
                        >
                          {memoryTotal(h.memory_facts)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
